import { APP_INITIALIZER, importProvidersFrom, type ApplicationConfig } from '@angular/core';
import { mergeApplicationConfig } from '@angular/core';
import { provideAuth0 } from '@auth0/auth0-angular';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { environment } from '../../environments/environment';
import { initializeKeycloak } from './keycloak-init';
import { shouldUseAuth0 } from './should-use-auth0';
import { AUTH0_READY } from './auth0-ready.token';

function isLocalhostHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname.startsWith('127.') || hostname === '0.0.0.0';
}

function requireNonEmpty(value: string | undefined | null, name: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`Missing required Auth0 config value: environment.auth0.${name}`);
  }
  return value;
}

export function buildBootstrapAppConfig(baseConfig: ApplicationConfig): ApplicationConfig {
  if (shouldUseAuth0() && environment.auth0) {
    const auth0Domain = requireNonEmpty(environment.auth0.auth0Domain, 'auth0Domain');
    const clientId = requireNonEmpty(environment.auth0.clientId, 'clientId');

    const hostname = window.location.hostname;
    const redirectUri = isLocalhostHostname(hostname)
      ? window.location.origin
      : environment.auth0.redirectUri || window.location.origin;

    return mergeApplicationConfig(baseConfig, {
      providers: [
        { provide: AUTH0_READY, useValue: true },
        provideAuth0({
          domain: auth0Domain,
          clientId,
          authorizationParams: {
            redirect_uri: redirectUri,
            audience: environment.auth0.audience,
            // Needed when using refresh tokens with Rotating Refresh Tokens.
            // If Auth0 doesn't issue a refresh token, auth0-spa-js throws "Missing Refresh Token".
            scope: 'openid profile email offline_access',
          },
          httpInterceptor: {
            allowedList: [
              {
                uri: `${environment.baseURL}*`,
                allowAnonymous: false,
              },
            ],
          },
          // Only enable when Auth0 app + API are configured to issue refresh tokens.
          useRefreshTokens: environment.auth0.useRefreshTokens === true,
          cacheLocation: 'localstorage',
        }),
      ],
    });
  }

  return mergeApplicationConfig(baseConfig, {
    providers: [
      importProvidersFrom(KeycloakAngularModule),
      {
        provide: APP_INITIALIZER,
        useFactory: initializeKeycloak,
        multi: true,
        deps: [KeycloakService],
      },
    ],
  });
}

